"use client"

import { useCallback, useRef, useState, type CSSProperties } from "react"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

const APPLY_URL = "https://apply.mbg.mn"
const COPY_THEN_OPEN_MS = 1000

/** Brighter conic ring: cream → gold → burgundy → violet → lavender (reads clearly on spin) */
const SPIN_RING_STYLE: CSSProperties = {
  background:
    "conic-gradient(from 0deg at 50% 50%, #fff7ed 0%, #fbbf24 11%, #d97706 20%, #7f1d1d 36%, #4c1d95 52%, #6366f1 68%, #c4b5fd 84%, #fff7ed 100%)",
}

export const ScholarshipCopyApplyButton = () => {
  const { toast } = useToast()
  const [isPending, setIsPending] = useState(false)
  const lockRef = useRef(false)

  const handleClick = useCallback(async () => {
    if (lockRef.current) return
    lockRef.current = true
    setIsPending(true)

    let copied = false
    try {
      await navigator.clipboard.writeText(window.location.href)
      copied = true
    } catch {
      copied = false
    }

    if (copied) {
      toast({
        title: "Холбоос хуулагдлаа",
        description: "Түр хүлээгээд apply.mbg.mn нээгдэнэ.",
      })
    } else {
      toast({
        variant: "destructive",
        title: "Холбоосыг хуулах боломжгүй",
        description: "Түр хүлээгээд apply.mbg.mn нээгдэнэ.",
      })
    }

    await new Promise<void>((resolve) => {
      setTimeout(resolve, COPY_THEN_OPEN_MS)
    })

    window.open(APPLY_URL, "_blank", "noopener,noreferrer")

    setIsPending(false)
    lockRef.current = false
  }, [toast])

  return (
    <span className="group/button-wrap relative inline-flex shrink-0">
      {/* Ambient glow — sibling to clipped button so blur isn’t clipped */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute -inset-5 z-0 rounded-full",
          "bg-[radial-gradient(ellipse_at_50%_50%,rgba(251,191,36,0.4)_0%,rgba(99,102,241,0.28)_48%,transparent_70%)]",
          "blur-2xl transition-opacity duration-300",
          "opacity-65 group-hover/button-wrap:opacity-95",
          isPending && "opacity-100"
        )}
      />

      <button
        type="button"
        disabled={isPending}
        onClick={handleClick}
        aria-busy={isPending}
        aria-label="Энэ хуудасны холбоосыг хуулаад, түр хүлээн apply.mbg.mn шинэ табд нээх"
        className={cn(
          "group relative z-[1] inline-flex h-12 shrink-0 overflow-hidden rounded-full p-[1px]",
          "shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_10px_36px_-8px_rgba(99,102,241,0.4),0_6px_20px_-6px_rgba(217,119,6,0.28)]",
          "transition-[box-shadow] duration-300",
          "hover:shadow-[0_0_0_1px_rgba(255,255,255,0.14),0_14px_44px_-6px_rgba(99,102,241,0.5),0_8px_28px_-6px_rgba(251,191,36,0.35)]",
          isPending &&
            "shadow-[0_0_0_1px_rgba(251,191,36,0.45),0_0_48px_-4px_rgba(251,191,36,0.5),0_14px_48px_-8px_rgba(99,102,241,0.55)]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/90 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:cursor-wait"
        )}
      >
        <span
          aria-hidden
          style={SPIN_RING_STYLE}
          className={cn(
            "absolute inset-[-1000%] motion-reduce:animate-none",
            isPending
              ? "animate-[spin_0.75s_linear_infinite]"
              : "animate-[spin_2.2s_linear_infinite] group-hover:animate-[spin_1.1s_linear_infinite]"
          )}
        />

        <span
          className={cn(
            "relative z-[1] inline-flex h-full min-h-[2.75rem] min-w-[13rem] cursor-pointer items-center justify-center rounded-full px-4 py-1 text-sm font-medium text-white",
            "bg-gradient-to-b from-zinc-800 via-zinc-950 to-black",
            "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.16),inset_0_-2px_0_0_rgba(0,0,0,0.55)]",
            "ring-1 ring-inset ring-white/[0.12]",
            "transition-[background-color,box-shadow] duration-300",
            "group-hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),inset_0_-2px_0_0_rgba(0,0,0,0.6)]",
            "group-disabled:cursor-wait"
          )}
        >
          {isPending ? "Түр хүлээнэ үү…" : "Холбоос хуулаад бүртгүүлэх"}
        </span>
      </button>
    </span>
  )
}
